import { useCallback, useState } from 'react';

import { useApi } from './useApi';

type UseNewHireQuestionsForLocationResult = {
  questions: NewHireQuestion[];
  stateCode: string | undefined;
  loading: boolean;
  fetchForLocation: (locationCode: string) => Promise<void>;
};

/**
 * Loads the new hire questions configured for a given work location, along
 * with the state-default questions and resolved state code. State is reset
 * before each fetch so stale questions never bleed across location changes.
 */
export const useNewHireQuestionsForLocation =
  (): UseNewHireQuestionsForLocationResult => {
    const api = useApi();
    const [questions, setQuestions] = useState<NewHireQuestion[]>([]);
    const [stateCode, setStateCode] = useState<string>();
    const [loading, setLoading] = useState(false);

    const fetchForLocation = useCallback(
      async (locationCode: string) => {
        setLoading(true);
        setQuestions([]);
        setStateCode(undefined);
        try {
          const res = await api.onboarding.getNewHireQuestions(locationCode);
          setQuestions([
            ...(res.results?.newHireQuestion || []),
            ...(res.results?.stateDefaultQuestion || []),
          ]);
          setStateCode(res.results?.stateCode);
        } catch (err) {
          console.error('Failed to fetch new hire questions:', err);
          setQuestions([]);
        } finally {
          setLoading(false);
        }
      },
      [api]
    );

    return { questions, stateCode, loading, fetchForLocation };
  };
